import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Policy from "@/pages/Policy";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";

const ScrollManager = ({ lenisRef }) => {
    const location = useLocation();
    useEffect(() => {
        const lenis = lenisRef.current;
        if (location.hash) {
            const timer = setTimeout(() => {
                const el = document.querySelector(location.hash);
                if (!el) return;
                if (lenis) lenis.scrollTo(el, { offset: -72 });
                else el.scrollIntoView({ behavior: "smooth" });
            }, 150);
            return () => clearTimeout(timer);
        }
        if (lenis) lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [location, lenisRef]);
    return null;
};

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading)
        return (
            <div className="flex min-h-[50vh] items-center justify-center text-sm text-ink/45" data-testid="route-loading">
                Loading…
            </div>
        );
    if (!user) return <Navigate to="/login" replace />;
    return children;
};

function App() {
    const lenisRef = useRef(null);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        const lenis = new Lenis({ duration: 1.2, smoothWheel: true, lerp: 0.1 });
        lenisRef.current = lenis;
        let rafId;
        const loop = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);

    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <ScrollManager lenisRef={lenisRef} />
                    <div className="min-h-screen bg-bone font-sans text-ink">
                        <Header />
                        <main>
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/products" element={<Products />} />
                                <Route path="/products/:slug" element={<ProductDetail />} />
                                <Route path="/policy" element={<Policy />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                                <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                            </Routes>
                        </main>
                        <Footer />
                    </div>
                    <Toaster position="top-center" richColors closeButton />
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
